import { FunctionComponent, useContext } from 'react';
import { useNavigate } from 'react-router';
import { ConfigContext } from '../configuration/ConfigContext';
import PaperLayout from '../layout/PaperLayout';
import { usePropertyAssociationContext } from '../propertyAssociation/PropertyAssociationContext';
import { useNavigationPaths } from '../propertyAssociation/useNavigationPaths';
import { ToastContext } from '../toaster/ToastContext';
import { ToastMessageType } from '../toaster/models/ToastMessageType';
import PinboardMessageForm from './components/PinboardMessageForm';
import { PinboardMessageFormModel, mapFormToCreateRequest } from './models/PinboardMessageFormModel';
import { createPinboardMessage } from './pinboardMessageService';

const formatDefaultPublishTime = (date: Date): string => {
    const roundedMinutes = date.getMinutes() >= 30 ? 30 : 0;
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(roundedMinutes).padStart(2, '0');

    return `${hours}:${minutes}`;
};

const createDefaultValues = (): PinboardMessageFormModel => {
    const now = new Date();

    return {
        id: '',
        title: '',
        text: '',
        filePaths: [],
        files: [],
        canUserAnswer: '',
        isPinned: false,
        target: '',
        date: now,
        time: formatDefaultPublishTime(now),
        endDate: undefined,
        endTime: undefined,
    };
};

const CreatePinboardMessage: FunctionComponent = () => {
    const { apiBaseUrl } = useContext(ConfigContext);
    const { addToast } = useContext(ToastContext);
    const { currentPropertyAssociationId, currentArea, isRBHyr, allPropertyAssociations } = usePropertyAssociationContext();
    const navigate = useNavigate();
    const paths = useNavigationPaths();

    const handleValidSubmit = (messageForm: PinboardMessageFormModel) => {
        const targetAssociations =
            messageForm.target === 'all'
                ? allPropertyAssociations.map((pa) => ({ propertyAssociationId: pa.propertyAssociationId, targetAreas: [] }))
                : messageForm.target === 'some'
                  ? messageForm.targetAssociations!.map((ta) => ({ propertyAssociationId: ta, targetAreas: [] }))
                  : messageForm.target === 'current'
                    ? [{ propertyAssociationId: currentPropertyAssociationId!, targetAreas: [] }]
                    : [{ propertyAssociationId: currentPropertyAssociationId!, targetAreas: [messageForm.target] }];

        createPinboardMessage(
            apiBaseUrl,
            mapFormToCreateRequest(messageForm, targetAssociations),
            () => {
                addToast('Meddelandet har skapats.');
                navigate(`${paths.adverts}/information`);
            },
            () => {
                addToast('Det gick inte att skapa meddelandet', undefined, ToastMessageType.Error);
            },
        );
    };

    const initialValues = {
        ...createDefaultValues(),
        target: currentArea?.propertyAssociationAreaId ? '' : 'current',
    };

    return (
        <PaperLayout
            heading="Skapa meddelande"
            tightenUp={true}
            mainClass="px-0 pt-3"
            preamble={
                <>
                    <strong>Information till boende</strong>
                    <p>Publicerade meddelanden ses av fastighetens boende på anslagstavlan i Mitt Boende-appen. {isRBHyr ? 'Hyresvärden' : 'Styrelsen'} står som avsändare.</p>
                </>
            }
        >
            <PinboardMessageForm isNew={true} messageForm={initialValues} onCancel={() => navigate(`${paths.adverts}/information`)} onValidSubmit={handleValidSubmit} submitBtnText="Publicera" />
        </PaperLayout>
    );
};

export default CreatePinboardMessage;
